import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Send, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useWhatsAppInbox, type WhatsAppMessage } from '@/hooks/useWhatsAppInbox';
import { MobileSearchBar } from '@/components/mobile/MobileSearchBar';
import { toast } from 'sonner';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { getWhatsAppErrorDescription } from '@/lib/whatsappErrors';

function normalizeWhatsAppTo(phone?: string | null) {
  if (!phone) return '';
  const trimmed = phone.trim();

  if (trimmed.startsWith('whatsapp:')) return trimmed;
  if (trimmed.startsWith('+')) return trimmed;

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';

  return `+${digits}`;
}

function formatTime(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export default function MobileWhatsApp() {
  const { id } = useParams();

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    conversations,
    conversationsLoading,
    fetchMessagesForContact,
    markAsRead,
    subscribeToMessages,
    appUserId,
    refetchConversations,
  } = useWhatsAppInbox();

  const selectedConversation = useMemo(() => {
    if (!id) return null;
    return (conversations || []).find((item) => item.contactId === id) || null;
  }, [conversations, id]);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    async function loadMessages() {
      setLoadingMessages(true);
      try {
        const data = await fetchMessagesForContact(id, selectedConversation?.ownerSessionId || appUserId || undefined);
        if (!cancelled) {
          setMessages(data || []);
          markAsRead(id);
        }
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    }

    loadMessages();

    return () => {
      cancelled = true;
    };
  }, [id, fetchMessagesForContact, markAsRead, selectedConversation?.ownerSessionId, appUserId]);

  useEffect(() => {
    if (!id) return;

    const unsubscribe = subscribeToMessages((message) => {
      if (message.contact_id !== id) return;

      setMessages((prev) => {
        if (prev.some((item) => item.id === message.id)) return prev;
        return [...prev, message];
      });

      if (message.direction === 'inbound') {
        markAsRead(id);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [id, subscribeToMessages, markAsRead]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const filteredConversations = useMemo(() => {
    const term = deferredSearch.trim().toLowerCase();
    if (!term) return conversations || [];

    return (conversations || []).filter((item) => {
      return item.contactName.toLowerCase().includes(term) || item.contactPhone.toLowerCase().includes(term);
    });
  }, [conversations, deferredSearch]);

  const handleSendMessage = async () => {
    if (!id || !selectedConversation || sending) return;

    const body = newMessage.trim();
    if (!body) return;

    const to = normalizeWhatsAppTo(selectedConversation.contactPhone);
    if (!to) return;

    const tempId = `temp-${Date.now()}`;
    const optimisticMessage: WhatsAppMessage = {
      id: tempId,
      contact_id: id,
      body,
      direction: 'outbound',
      owner_session_id: appUserId || null,
      sent_by_app_user_id: appUserId || null,
      created_at: new Date().toISOString(),
      read_at: null,
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    setNewMessage('');
    setSending(true);

    try {
      const { error } = await supabase.functions.invoke('twilio-whatsapp', {
        body: {
          action: 'sendMessage',
          to,
          body,
          contactId: id,
        },
      });

      if (error) throw error;

      await refetchConversations();
      const latest = await fetchMessagesForContact(id, selectedConversation.ownerSessionId || appUserId || undefined);
      setMessages(latest || []);
    } catch {
      setMessages((prev) => prev.filter((item) => item.id !== tempId));
      setNewMessage(body);
      toast.error('Nao foi possivel enviar a mensagem.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  if (id) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b bg-background p-3">
          <p className="text-sm font-semibold">{selectedConversation?.contactName || 'Conversa'}</p>
          <p className="text-xs text-muted-foreground">{selectedConversation?.contactPhone || ''}</p>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 space-y-2 overflow-y-auto overscroll-contain p-3"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {loadingMessages ? <p className="text-sm text-muted-foreground">Carregando mensagens...</p> : null}

          {!loadingMessages && messages.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem mensagens para este contato.</p>
          ) : null}

          {messages.map((message) => {
            const outbound = message.direction !== 'inbound';

            return (
              <div key={message.id} className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[86%] rounded-2xl px-3 py-2 text-sm ${
                    outbound ? 'bg-primary text-primary-foreground' : 'border bg-card text-foreground'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{message.body || '[sem texto]'}</p>
                  <div className={`mt-1 flex items-center gap-1 ${outbound ? 'justify-end' : ''}`}>
                    <span className={`text-[10px] ${outbound ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                      {formatTime(message.created_at)}
                    </span>
                    {outbound && (message.status === 'failed' || message.status === 'undelivered') && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertCircle className="h-3 w-3 text-destructive cursor-pointer" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="text-xs font-medium">
                              {message.status === 'undelivered' ? 'Não entregue' : 'Falha no envio'}
                              {message.error_code && ` (Erro ${message.error_code})`}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {getWhatsAppErrorDescription(message.error_code)}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t bg-background p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center gap-2">
            <Input
              value={newMessage}
              onChange={(event) => setNewMessage(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite uma mensagem..."
              aria-label="Digite uma mensagem"
              disabled={sending}
              className="h-10"
            />
            <Button
              type="button"
              size="icon"
              disabled={sending || !newMessage.trim()}
              onClick={handleSendMessage}
              aria-label="Enviar mensagem"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-3">
      <MobileSearchBar
        value={search}
        onChange={setSearch}
        placeholder="Buscar conversa..."
        ariaLabel="Buscar conversa"
      />

      {conversationsLoading ? <p className="text-sm text-muted-foreground">Carregando conversas...</p> : null}

      <div className="space-y-2">
        {filteredConversations.map((conversation) => (
          <Link
            key={conversation.contactId}
            to={`/m/whatsapp/${conversation.contactId}`}
            className="block rounded-lg border bg-card p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="truncate text-sm font-semibold">{conversation.contactName}</p>
              {conversation.unreadCount > 0 ? (
                <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                  {conversation.unreadCount}
                </span>
              ) : null}
            </div>
            <p className="mt-1 truncate text-xs text-muted-foreground">{conversation.lastMessage || 'Sem mensagem'}</p>
            <p className="mt-1 text-[10px] text-muted-foreground">{formatTime(conversation.lastMessageAt)}</p>
          </Link>
        ))}
      </div>

      {!conversationsLoading && filteredConversations.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma conversa encontrada.</p>
      ) : null}
    </div>
  );
}
